import ClientFactory from './ClientFactory.js';
import { isEntityId } from './utils/entityUtils.js';
/**
 * API Connector provides access to Showell APIs via Clients
 *
 * The connector configures the Clients and provides mechanisms
 * for common tasks such as authentication and account selection.
 */
export default class ShowellApi {
    _options;
    _factory;
    constructor(options) {
        this._options = options;
    }
    get factory() {
        if (!this._factory) {
            this._factory = new ClientFactory(this._options);
        }
        return this._factory;
    }
    configure(options) {
        this._options = { ...this._options, ...options };
        this._factory?.setOptions(this._options);
    }
    /**
     * Authenticate with the API
     */
    async authenticate(credentials) {
        const accessToken = await this.client('app').requestAccessToken(credentials);
        this.configure({ accessToken });
        return this;
    }
    /**
     * Use specific account for API requests rather than user's home account
     */
    async useAccount(account) {
        let accountId;
        if (isEntityId(account)) {
            accountId = account;
        }
        else {
            const specs = await this.client('account').find({
                query: account,
                specs: { id: true },
            });
            if (!specs) {
                throw new Error(`Account "${account}" not found`);
            }
            accountId = specs.id;
        }
        this.configure({ accountId });
        return this;
    }
    /**
     * Reset back to using default account
     */
    useDefaultAccount() {
        this.configure({ accountId: undefined });
        return this;
    }
    /**
     * Set access token used for authentication
     */
    setAccessToken(accessToken) {
        this.configure({ accessToken });
        return this;
    }
    /**
     * Retrieve access token used for authentication
     */
    getAccessToken() {
        return this._options.accessToken;
    }
    /**
     * Use client
     *
     * There are number of clients available, including a
     * generic 'api' client and specific clients such as 'fileClient' and
     * 'userClient'. The specific clients usually provide convenience methods
     * for accessing the endpoints represented by the client. However, all of the
     * endpoints consumed by these clients are also consumable by the generic 'api' client.
     */
    client(client) {
        return this.factory.getClient(client);
    }
}
